<template>
  <div class="project-performance-view">
    <!-- Loading State -->
    <LoadingIndicator v-if="loading" message="Loading project data..." />

    <!-- Performance Data -->
    <div v-else-if="performanceData" class="space-y-4">
      <!-- Overall Project Stats -->
      <div class="bg-white border border-gray-200">
        <ComponentHeader
          title="Project Performance"
          :heading-level="4"
          header-class="bg-gray-50"
        >
          <label class="text-xs font-medium text-gray-700">View:</label>
          <div class="flex gap-2">
            <button
              @click="setViewMode('practice')"
              :class="[
                'px-2 py-1 text-xs rounded border',
                viewMode === 'practice'
                  ? 'bg-primary-500 text-white border-orange-500'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              ]"
            >
              By Practice
            </button>
            <button
              @click="setViewMode('task')"
              :class="[
                'px-2 py-1 text-xs rounded border',
                viewMode === 'task'
                  ? 'bg-primary-500 text-white border-orange-500'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              ]"
            >
              By Task
            </button>
            <button
              @click="setViewMode('hierarchy')"
              :class="[
                'px-2 py-1 text-xs rounded border',
                viewMode === 'hierarchy'
                  ? 'bg-primary-500 text-white border-orange-500'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              ]"
            >
              By Entity
            </button>
          </div>
          <select
            v-if="viewMode === 'hierarchy'"
            v-model="selectedEntityTypeId"
            @change="onEntityTypeChange"
            class="px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
          >
            <option :value="null">Select entity type...</option>
            <option v-for="type in entityTypes" :key="type.id" :value="type.id">
              {{ type.type_name }}
            </option>
          </select>
        </ComponentHeader>

        <div class="p-4">
          <div class="grid grid-cols-3 gap-3 mb-3">
            <div class="text-center">
              <div class="text-xs text-gray-600 mb-1">Total Tasks</div>
              <div class="text-lg font-semibold text-gray-900">{{ performanceData.total_actions }}</div>
            </div>

            <div class="text-center">
              <div class="text-xs text-gray-600 mb-1">Completed</div>
              <div class="text-lg font-semibold text-gray-900">{{ performanceData.completed_actions }}</div>
            </div>

            <div class="text-center">
              <div class="text-xs text-gray-600 mb-1">Completion</div>
              <div class="text-lg font-semibold text-gray-900">{{ performanceData.completion_percentage }}%</div>
            </div>
          </div>

          <!-- Overall Progress Bar -->
          <div class="w-full bg-gray-200 rounded h-2">
            <div
              class="bg-primary-500 h-2 rounded transition-all duration-300"
              :style="{ width: performanceData.completion_percentage + '%' }"
            ></div>
          </div>
        </div>
      </div>

      <!-- BY PRACTICE VIEW -->
      <div v-if="viewMode === 'practice'" class="space-y-2 max-h-[500px] overflow-y-auto">
        <div
          v-for="practice in performanceData.practices"
          :key="`practice-${practice.entity_id}`"
          class="bg-white border border-gray-200"
        >
          <!-- Practice Header -->
          <div
            class="flex justify-between items-center p-3 cursor-pointer hover:bg-gray-50"
            @click="togglePractice(practice.entity_id)"
          >
            <div class="flex items-center gap-2 flex-1">
              <font-awesome-icon
                :icon="['fas', expandedPractices.includes(practice.entity_id) ? 'chevron-down' : 'chevron-right']"
                class="text-gray-400 text-xs"
              />
              <div class="flex-1">
                <div class="sensitive text-sm font-medium text-gray-800" data-spoof-kind="practice">{{ practice.entity_name }}</div>
                <div class="text-xs text-gray-500">
                  {{ practice.completed_tasks }} / {{ practice.total_tasks }} tasks
                </div>
              </div>
            </div>

            <div class="flex items-center gap-3">
              <div class="text-sm font-medium text-gray-700">
                {{ practice.completion_percentage }}%
              </div>
              <div class="w-24 bg-gray-200 rounded h-1.5">
                <div
                  class="h-1.5 rounded transition-all duration-300"
                  :class="getCompletionBarClass(practice.completion_percentage)"
                  :style="{ width: practice.completion_percentage + '%' }"
                ></div>
              </div>
            </div>
          </div>

          <!-- Tasks -->
          <div v-if="expandedPractices.includes(practice.entity_id)" class="border-t border-gray-200 bg-gray-50">
            <div class="p-3 space-y-1">
              <div
                v-for="task in practice.tasks"
                :key="task.action_id"
                class="flex items-center justify-between p-2 bg-white border border-gray-200 rounded"
              >
                <div class="flex items-center gap-2 flex-1">
                  <font-awesome-icon
                    :icon="['fas', task.is_completed ? 'check-circle' : 'circle']"
                    :class="task.is_completed ? 'text-green-600' : 'text-gray-300'"
                    class="text-sm"
                  />
                  <div class="flex-1">
                    <div class="text-xs font-medium text-gray-800">{{ task.action_name }}</div>
                    <div class="text-xs text-gray-600">{{ task.description }}</div>
                  </div>
                </div>

                <div class="text-xs text-gray-500 ml-3">
                  <div v-if="task.completed_date">
                    {{ formatDate(task.completed_date) }}
                  </div>
                  <div v-else-if="task.due_date">
                    Due: {{ formatDate(task.due_date) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- BY TASK VIEW -->
      <div v-if="viewMode === 'task'" class="space-y-2 max-h-[500px] overflow-y-auto">
        <div
          v-for="(taskGroup, taskName) in groupByTask"
          :key="taskName"
          class="bg-white border border-gray-200"
        >
          <!-- Task Header -->
          <div
            class="flex justify-between items-center p-3 cursor-pointer hover:bg-gray-50"
            @click="toggleTask(taskName)"
          >
            <div class="flex items-center gap-2 flex-1">
              <font-awesome-icon
                :icon="['fas', expandedTasks.includes(taskName) ? 'chevron-down' : 'chevron-right']"
                class="text-gray-400 text-xs"
              />
              <div class="flex-1">
                <div class="text-sm font-medium text-gray-800">{{ taskName }}</div>
                <div class="text-xs text-gray-500">
                  {{ taskGroup.completed }} / {{ taskGroup.total }} practices
                </div>
              </div>
            </div>

            <div class="flex items-center gap-3">
              <div class="text-sm font-medium text-gray-700">
                {{ taskGroup.percentage }}%
              </div>
              <div class="w-24 bg-gray-200 rounded h-1.5">
                <div
                  class="h-1.5 rounded transition-all duration-300"
                  :class="getCompletionBarClass(taskGroup.percentage)"
                  :style="{ width: taskGroup.percentage + '%' }"
                ></div>
              </div>
            </div>
          </div>

          <!-- Practices for this task -->
          <div v-if="expandedTasks.includes(taskName)" class="border-t border-gray-200 bg-gray-50">
            <div class="p-3 space-y-1">
              <div
                v-for="item in taskGroup.items"
                :key="item.practice_id"
                class="flex items-center justify-between p-2 bg-white border border-gray-200 rounded"
              >
                <div class="flex items-center gap-2 flex-1">
                  <font-awesome-icon
                    :icon="['fas', item.is_completed ? 'check-circle' : 'circle']"
                    :class="item.is_completed ? 'text-green-600' : 'text-gray-300'"
                    class="text-sm"
                  />
                  <div class="sensitive text-xs font-medium text-gray-800" data-spoof-kind="practice">{{ item.practice_name }}</div>
                </div>

                <div class="text-xs text-gray-500 ml-3">
                  <div v-if="item.completed_date">
                    {{ formatDate(item.completed_date) }}
                  </div>
                  <div v-else-if="item.due_date">
                    Due: {{ formatDate(item.due_date) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- HIERARCHY VIEW -->
      <div v-if="viewMode === 'hierarchy'" class="space-y-2 max-h-[500px] overflow-y-auto">
        <div v-if="!selectedEntityTypeId" class="bg-white border border-gray-200 p-8 text-center">
          <p class="text-xs text-gray-500">Select an entity type to view hierarchy</p>
        </div>

        <!-- Parent Entities -->
        <div
          v-for="parentEntity in performanceData?.hierarchy?.parent_entities"
          :key="`parent-${parentEntity.entity_id}`"
          class="bg-white border border-gray-200"
        >
          <!-- Parent Entity Header -->
          <div
            class="flex justify-between items-center p-3 cursor-pointer hover:bg-gray-50"
            @click="toggleLiaison(parentEntity.entity_id)"
          >
            <div class="flex items-center gap-2 flex-1">
              <font-awesome-icon
                :icon="['fas', expandedLiaisons.includes(parentEntity.entity_id) ? 'chevron-down' : 'chevron-right']"
                class="text-gray-400 text-xs"
              />
              <div class="flex-1">
                <div class="text-sm font-semibold text-gray-800">📁 <span class="sensitive" :data-spoof-kind="entitySpoofKind(selectedEntityTypeName, parentEntity.entity_name)">{{ parentEntity.entity_name }}</span></div>
                <div class="text-xs text-gray-500">
                  {{ parentEntity.practices.length }} practices • {{ parentEntity.completed_tasks }} / {{ parentEntity.total_tasks }} tasks
                </div>
              </div>
            </div>

            <div class="flex items-center gap-3">
              <div class="text-sm font-medium text-gray-700">
                {{ parentEntity.completion_percentage }}%
              </div>
              <div class="w-24 bg-gray-200 rounded h-1.5">
                <div
                  class="h-1.5 rounded transition-all duration-300"
                  :class="getCompletionBarClass(parentEntity.completion_percentage)"
                  :style="{ width: parentEntity.completion_percentage + '%' }"
                ></div>
              </div>
            </div>
          </div>

          <!-- Practices under Parent Entity -->
          <div v-if="expandedLiaisons.includes(parentEntity.entity_id)" class="border-t border-gray-200 bg-gray-50">
            <div class="p-2 space-y-2">
              <div
                v-for="practice in parentEntity.practices"
                :key="`practice-${practice.entity_id}`"
                class="bg-white border border-gray-200 rounded"
              >
                <!-- Practice Header -->
                <div
                  class="flex justify-between items-center p-2 cursor-pointer hover:bg-gray-50"
                  @click="togglePractice(practice.entity_id)"
                >
                  <div class="flex items-center gap-2 flex-1">
                    <font-awesome-icon
                      :icon="['fas', expandedPractices.includes(practice.entity_id) ? 'chevron-down' : 'chevron-right']"
                      class="text-gray-400 text-xs ml-4"
                    />
                    <div class="flex-1">
                      <div class="sensitive text-sm font-medium text-gray-800" data-spoof-kind="practice">{{ practice.entity_name }}</div>
                      <div class="text-xs text-gray-500">
                        {{ practice.completed_tasks }} / {{ practice.total_tasks }} tasks
                      </div>
                    </div>
                  </div>

                  <div class="flex items-center gap-3">
                    <div class="text-sm font-medium text-gray-700">
                      {{ practice.completion_percentage }}%
                    </div>
                    <div class="w-20 bg-gray-200 rounded h-1.5">
                      <div
                        class="h-1.5 rounded transition-all duration-300"
                        :class="getCompletionBarClass(practice.completion_percentage)"
                        :style="{ width: practice.completion_percentage + '%' }"
                      ></div>
                    </div>
                  </div>
                </div>

                <!-- Tasks under Practice -->
                <div v-if="expandedPractices.includes(practice.entity_id)" class="border-t border-gray-200 bg-gray-50">
                  <div class="p-2 space-y-1">
                    <div
                      v-for="task in practice.tasks"
                      :key="task.action_id"
                      class="flex items-center justify-between p-2 bg-white border border-gray-200 rounded ml-8"
                    >
                      <div class="flex items-center gap-2 flex-1">
                        <font-awesome-icon
                          :icon="['fas', task.is_completed ? 'check-circle' : 'circle']"
                          :class="task.is_completed ? 'text-green-600' : 'text-gray-300'"
                          class="text-sm"
                        />
                        <div class="flex-1">
                          <div class="text-xs font-medium text-gray-800">{{ task.action_name }}</div>
                          <div class="text-xs text-gray-600">{{ task.description }}</div>
                        </div>
                      </div>

                      <div class="text-xs text-gray-500 ml-3">
                        <div v-if="task.completed_date">
                          {{ formatDate(task.completed_date) }}
                        </div>
                        <div v-else-if="task.due_date">
                          Due: {{ formatDate(task.due_date) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Practices without Parent -->
        <div
          v-if="performanceData?.hierarchy?.practices_without_parent?.length > 0"
          class="bg-white border border-gray-200"
        >
          <div
            class="flex justify-between items-center p-3 cursor-pointer hover:bg-gray-50"
            @click="toggleUnassigned()"
          >
            <div class="flex items-center gap-2 flex-1">
              <font-awesome-icon
                :icon="['fas', showUnassigned ? 'chevron-down' : 'chevron-right']"
                class="text-gray-400 text-xs"
              />
              <div class="flex-1">
                <div class="text-sm font-semibold text-gray-800">
                  📂 Practices without {{ selectedEntityTypeName }}
                </div>
                <div class="text-xs text-gray-500">
                  {{ performanceData.hierarchy.practices_without_parent.length }} practices
                </div>
              </div>
            </div>
          </div>

          <div v-if="showUnassigned" class="border-t border-gray-200 bg-gray-50">
            <div class="p-2 space-y-2">
              <div
                v-for="practice in performanceData.hierarchy.practices_without_parent"
                :key="`practice-unassigned-${practice.entity_id}`"
                class="bg-white border border-gray-200 rounded"
              >
                <div
                  class="flex justify-between items-center p-2 cursor-pointer hover:bg-gray-50"
                  @click="togglePractice(practice.entity_id)"
                >
                  <div class="flex items-center gap-2 flex-1">
                    <font-awesome-icon
                      :icon="['fas', expandedPractices.includes(practice.entity_id) ? 'chevron-down' : 'chevron-right']"
                      class="text-gray-400 text-xs ml-4"
                    />
                    <div class="flex-1">
                      <div class="sensitive text-sm font-medium text-gray-800" data-spoof-kind="practice">{{ practice.entity_name }}</div>
                      <div class="text-xs text-gray-500">
                        {{ practice.completed_tasks }} / {{ practice.total_tasks }} tasks
                      </div>
                    </div>
                  </div>

                  <div class="flex items-center gap-3">
                    <div class="text-sm font-medium text-gray-700">
                      {{ practice.completion_percentage }}%
                    </div>
                    <div class="w-20 bg-gray-200 rounded h-1.5">
                      <div
                        class="h-1.5 rounded transition-all duration-300"
                        :class="getCompletionBarClass(practice.completion_percentage)"
                        :style="{ width: practice.completion_percentage + '%' }"
                      ></div>
                    </div>
                  </div>
                </div>

                <div v-if="expandedPractices.includes(practice.entity_id)" class="border-t border-gray-200 bg-gray-50">
                  <div class="p-2 space-y-1">
                    <div
                      v-for="task in practice.tasks"
                      :key="task.action_id"
                      class="flex items-center justify-between p-2 bg-white border border-gray-200 rounded ml-8"
                    >
                      <div class="flex items-center gap-2 flex-1">
                        <font-awesome-icon
                          :icon="['fas', task.is_completed ? 'check-circle' : 'circle']"
                          :class="task.is_completed ? 'text-green-600' : 'text-gray-300'"
                          class="text-sm"
                        />
                        <div class="flex-1">
                          <div class="text-xs font-medium text-gray-800">{{ task.action_name }}</div>
                          <div class="text-xs text-gray-600">{{ task.description }}</div>
                        </div>
                      </div>

                      <div class="text-xs text-gray-500 ml-3">
                        <div v-if="task.completed_date">
                          {{ formatDate(task.completed_date) }}
                        </div>
                        <div v-else-if="task.due_date">
                          Due: {{ formatDate(task.due_date) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import { entitySpoofKind } from '@/utils/demoSpoof';

export default {
  name: 'ProjectPerformanceView',

  components: {
    FontAwesomeIcon,
    LoadingIndicator,
    ComponentHeader
  },

  props: {
    project: { type: Object, required: true }
  },

  data() {
    return {
      performanceData: null,
      loading: false,
      viewMode: 'practice', // 'practice', 'task', 'hierarchy'
      expandedLiaisons: [],
      expandedPractices: [],
      expandedTasks: [],
      showUnassigned: false,
      entityTypes: [],
      selectedEntityTypeId: null
    };
  },

  computed: {
    selectedEntityTypeName() {
      if (!this.selectedEntityTypeId) return '';
      const type = this.entityTypes.find(t => t.id === this.selectedEntityTypeId);
      return type ? type.type_name : '';
    },

    groupByTask() {
      if (!this.performanceData?.practices) return {};

      const taskGroups = {};

      this.performanceData.practices.forEach(practice => {
        practice.tasks.forEach(task => {
          if (!taskGroups[task.action_name]) {
            taskGroups[task.action_name] = {
              total: 0,
              completed: 0,
              items: []
            };
          }

          taskGroups[task.action_name].total++;
          if (task.is_completed) {
            taskGroups[task.action_name].completed++;
          }

          taskGroups[task.action_name].items.push({
            practice_id: practice.entity_id,
            practice_name: practice.entity_name,
            action_id: task.action_id,
            is_completed: task.is_completed,
            completed_date: task.completed_date,
            due_date: task.due_date
          });
        });
      });

      Object.keys(taskGroups).forEach(taskName => {
        const group = taskGroups[taskName];
        group.percentage = Math.round((group.completed / group.total) * 100);
        group.items.sort((a, b) => a.practice_name.localeCompare(b.practice_name));
      });

      return Object.entries(taskGroups)
        .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
        .reduce((acc, [name, data]) => {
          acc[name] = data;
          return acc;
        }, {});
    }
  },

  created() {
    this.loadEntityTypes();
    this.loadProjectPerformance();
  },

  watch: {
    'project.id'() {
      this.expandedLiaisons = [];
      this.expandedPractices = [];
      this.expandedTasks = [];
      this.loadProjectPerformance();
    }
  },

  methods: {
    entitySpoofKind,
    async loadEntityTypes() {
      const response = await axios.get('api/entity-types/');
      this.entityTypes = (response.data.results || response.data).sort((a, b) =>
        a.type_name.localeCompare(b.type_name)
      );
    },

    onEntityTypeChange() {
      this.loadProjectPerformance();
    },

    async loadProjectPerformance() {
      if (!this.project?.id) return;

      this.loading = true;

      const params = {};
      if (this.viewMode === 'hierarchy' && this.selectedEntityTypeId) {
        params.parent_entity_type_id = this.selectedEntityTypeId;
      }

      const response = await axios.get(`api/graph-projects/${this.project.id}/performance/`, { params });
      this.performanceData = response.data;

      // Expand first 2 parent entities/practices by default
      if (this.viewMode === 'hierarchy' && this.performanceData.hierarchy.parent_entities.length > 0) {
        this.expandedLiaisons = this.performanceData.hierarchy.parent_entities
          .slice(0, 2)
          .map(l => l.entity_id);
      } else if (this.viewMode === 'practice' && this.performanceData.practices.length > 0) {
        this.expandedPractices = this.performanceData.practices
          .slice(0, 3)
          .map(p => p.entity_id);
      }

      this.loading = false;
    },

    setViewMode(mode) {
      this.viewMode = mode;
      // Reset expansion state when changing views
      this.expandedLiaisons = [];
      this.expandedPractices = [];
      this.expandedTasks = [];
      this.loadProjectPerformance();
    },

    toggleLiaison(liaisonId) {
      const index = this.expandedLiaisons.indexOf(liaisonId);
      if (index > -1) {
        this.expandedLiaisons.splice(index, 1);
      } else {
        this.expandedLiaisons.push(liaisonId);
      }
    },

    togglePractice(practiceId) {
      const index = this.expandedPractices.indexOf(practiceId);
      if (index > -1) {
        this.expandedPractices.splice(index, 1);
      } else {
        this.expandedPractices.push(practiceId);
      }
    },

    toggleTask(taskName) {
      const index = this.expandedTasks.indexOf(taskName);
      if (index > -1) {
        this.expandedTasks.splice(index, 1);
      } else {
        this.expandedTasks.push(taskName);
      }
    },

    toggleUnassigned() {
      this.showUnassigned = !this.showUnassigned;
    },

    getCompletionBarClass(percentage) {
      if (percentage >= 80) return 'bg-green-600';
      if (percentage >= 50) return 'bg-yellow-500';
      return 'bg-red-500';
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  }
};
</script>

<style scoped>
.project-performance-view {
  font-size: 12px;
}
</style>
